import axios from 'axios';
import { shouldInvalidateSession } from './sessionError';

const API_BASE = '/api/v1';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (shouldInvalidateSession(err, localStorage.getItem('token')) && window.location.pathname !== '/login') {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  familiarization: () => api.get('/familiarization/overview'),
  team: () => api.get('/auth/team'),
  deleteTeamMember: (userId) => api.delete(`/auth/team/${userId}`),
  createInvite: (role = 'viewer') => api.post('/auth/invite', null, { params: { role } }),
  updateAccess: (id, data) => api.patch(`/auth/team/${id}/access`, data),
  audit: (params) => api.get('/audit', { params }),
  acceptInvite: (data) => api.post('/auth/invite/accept', data),
};

export const clientsAPI = {
  expiry:(id,days=30)=>api.get(`/clients/${id}/expiry`,{params:{days}}),
  tableDetails:client_ids=>api.post('/clients/table-details',{client_ids}),
  timeline: (id, params = {}) => api.get(`/clients/${id}/timeline`, { params }),
  list: (skip = 0, limit = 50, search = null, filters = {}) => {
    let url = `/clients?skip=${skip}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    const extra=new URLSearchParams(filters).toString();
    return api.get(url+(extra?'&'+extra:''));
  },
  filtered: (data) => api.post('/clients/filtered', data),
  segmentsBatch: (clientIds) => api.post('/clients/segments-batch', { client_ids: clientIds }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.patch(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  balance: (phone) => api.get(`/clients/balance/${encodeURIComponent(phone)}`),
  tierStats: () => api.get('/clients/stats/tiers'),
  importFile: (file, updateExisting = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/clients/import?update_existing=${updateExisting}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importStatus: (taskId) => api.get(`/clients/import/status/${taskId}`),
};

export const transactionsAPI = {
  accrue: (data) => api.post('/transactions/accrue', data),
  deduct: (data) => api.post('/transactions/deduct', data),
  all: (skip = 0, limit = 50, filters = {}) => api.get('/transactions/all',{params:{skip,limit,...filters}}),
  history: (clientId, skip = 0, limit = 50) =>
    api.get(`/transactions/history/${clientId}?skip=${skip}&limit=${limit}`),
};

export const ordersAPI = {
  attributionOptions: () => api.get('/orders/attribution-options'),
  create: (data) => api.post('/orders', data),
  confirm: (orderId) => api.post('/orders/confirm', { order_id: orderId }),
  cancel: (orderId) => api.post('/orders/cancel', { order_id: orderId }),
  all: (skip = 0, limit = 50, search = null, filters = {}) => {
    const params=new URLSearchParams({skip:String(skip),limit:String(limit)});
    if(search)params.set('search',search);
    ['status','place_ids','exclude_place_ids','date_from','date_to','min_total','max_total','client_type','source','rfm_segments','exclude_rfm_segments','sort_by','sort_dir','chain_ids','campaign_ids','client_id'].forEach(key=>{
      const value=filters[key];if(value!==undefined&&value!==null&&value!=='')params.set(key,String(value));
    });
    const url='/orders/all?'+params.toString();
    return api.get(url);
  },
  get: (id) => api.get(`/orders/${id}`),
  byClient: (clientId) => api.get(`/orders/client/${clientId}`),
  importCSV: (formData, accrueBonuses = false, defaultStatus = 'confirmed') => api.post(`/orders/import?accrue_bonuses=${accrueBonuses}&default_status=${defaultStatus}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importStatus: (taskId) => api.get(`/orders/import/status/${taskId}`),
};

export default api;


export const analyticsAPI = {
  bonusOverview: (query,all=false)=>api.get(`/analytics/bonus-overview?period=${query}&cohort_all=${all}`),
  dashboardV2: (period = '30d') => api.get(`/analytics/dashboard-v2?period=${period}`),
  dashboard: () => api.get('/analytics/dashboard'),
  insights: () => api.get('/analytics/insights'),
  rfm: () => api.get('/analytics/rfm'),
  atRisk: (minBalance = 0, daysInactive = 30) =>
    api.get(`/analytics/at-risk?min_balance=${minBalance}&days_inactive=${daysInactive}`),
  rfmStats: () => api.get('/analytics/rfm-stats'),
  rfmConfig: () => api.get('/analytics/rfm-config'),
  updateRfmConfig: (data) => api.put('/analytics/rfm-config', data),
};

export const commsAPI = {
  abAnalysis: id => api.get(`/communications/campaigns/${id}/ab-analysis`),
  abReview: (id,preview_hash) => api.post(`/communications/campaigns/${id}/ab-review`,{preview_hash,confirm:true}),
  generateText: (data) => api.post('/communications/generate-text', data),
  preview: (data) => api.post('/communications/preview', data),
  botOptions: () => api.get('/communications/bot-options'),
  uploadAttachment: file => api.post('/communications/attachments?filename='+encodeURIComponent(file.name),file,{headers:{'Content-Type':'application/octet-stream'}}),
  count: (segmentFilter, channel, botIds, botAudience) => api.post(`/communications/count?channel=${channel}${botAudience?'&bot_audience='+botAudience:''}${botIds?botIds.map(id=>'&bot_ids='+encodeURIComponent(id)).join(''):''}`, segmentFilter),
  createCampaign: (data) => api.post('/communications/campaigns', data),
  launchCampaign: (id) => api.post(`/communications/campaigns/${id}/launch`),
  scheduleCampaign: (id, scheduled_at) => api.post(`/communications/campaigns/${id}/schedule`, { scheduled_at }),
  cancelCampaign: (id) => api.post(`/communications/campaigns/${id}/cancel`),
  campaigns: (skip = 0, limit = 50, filters={}) => api.get('/communications/campaigns',{params:{skip,limit,...filters}}),
  attributedOrders: (id,skip=0) => api.get(`/communications/campaigns/${id}/attributed-orders`,{params:{skip,limit:25}}),
  recommendations: () => api.get('/communications/recommendations'),
};


export const loyaltyAPI = {
  expirySettings:()=>api.get('/loyalty/expiry-settings'),
  saveExpirySettings:data=>api.put('/loyalty/expiry-settings',data),
  settings:()=>api.get('/loyalty/settings'),
  saveSettings:data=>api.put('/loyalty/settings',data),
  tiers: () => api.get('/loyalty/tiers'),
  createTier: (data) => api.post('/loyalty/tiers', data),
  updateTier: (id, data) => api.patch(`/loyalty/tiers/${id}`, data),
  deleteTier: (id) => api.delete(`/loyalty/tiers/${id}`),
  seedDefaults: () => api.post('/loyalty/tiers/seed'),
};


export const integrationsAPI = {
  providers: () => api.get('/integrations/providers'),
  list: () => api.get('/integrations'),
  create: (data) => api.post('/integrations', data),
  update: (id, data) => api.patch(`/integrations/${id}`, data),
  delete: (id) => api.delete(`/integrations/${id}`),
};


export const promocodesAPI = {
  list: (skip = 0, limit = 200, search, tab = 'active', dateFrom, dateTo, sortBy, sortDir) => {
    let url = `/promocodes?skip=${skip}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (tab) url += `&tab=${tab}`;
    if (dateFrom) url += `&date_from=${dateFrom}`;
    if (dateTo) url += `&date_to=${dateTo}`;
    if (sortBy) url += `&sort_by=${sortBy}`;
    if (sortDir) url += `&sort_dir=${sortDir}`;
    return api.get(url);
  },
  create: (data) => api.post('/promocodes', data),
  update: (id, data) => api.patch(`/promocodes/${id}`, data),
  delete: (id) => api.delete(`/promocodes/${id}`),
  validate: (data) => api.post('/promocodes/validate', data),
  groups: () => api.get('/promocodes/groups'),
  createGroup: (data) => api.post('/promocodes/groups', data),
  updateGroup: (id, data) => api.put(`/promocodes/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/promocodes/groups/${id}`),
};

export const placesAPI = {
  list: () => api.get('/places'),
  create: (data) => api.post('/places', data),
  update: (id, data) => api.put(`/places/${id}`, data),
  delete: (id) => api.delete(`/places/${id}`),
};

export const emailTemplatesAPI = {
  list: () => api.get('/email-templates'),
  get: (id) => api.get(`/email-templates/${id}`),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
};

export const savedSegmentsAPI = {
  list: () => api.get('/segments'),
  stats: (period = 'month') => api.get(`/segments/stats?period=${period}`),
  create: (data) => api.post('/segments', data),
  update: (id, data) => api.put(`/segments/${id}`, data),
  delete: (id) => api.delete(`/segments/${id}`),
};

export const chainsAPI = {
  attributedOrders: (id, skip=0) => api.get(`/chains/${id}/attributed-orders`,{params:{skip,limit:25}}),
  transcribe: (audio, signal) => {const form=new FormData();form.append('audio',audio,`voice-brief.${audio.type.includes('mp4')?'mp4':audio.type.includes('ogg')?'ogg':'webm'}`);return api.post('/chains/assistant/transcribe',form,{timeout:60000,signal,headers:{'Content-Type':undefined}});},
  assistant: (data) => api.post('/chains/assistant/generate', data, {timeout:50000}),
  list: () => api.get('/chains'),
  get: (id) => api.get(`/chains/${id}`),
  create: (data) => api.post('/chains', data),
  update: (id, data) => api.put(`/chains/${id}`, data),
  delete: (id) => api.delete(`/chains/${id}`),
  toggle: (id) => api.post(`/chains/${id}/toggle`),
  test: (id, phone) => api.post(`/chains/${id}/test`, { client_phone: phone }),
  tick: () => api.post('/chains/tick'),
  fireTrigger: (triggerType, phone) => api.post('/chains/fire-trigger', { trigger_type: triggerType, client_phone: phone }),
  recalcTiers: () => api.post('/chains/recalc-tiers'),
};


export const pixelAPI = {
  events: (skip = 0, limit = 50, event_type = null, days = 30) => {
    let url = `/pixel/events?skip=${skip}&limit=${limit}&days=${days}`;
    if (event_type) url += `&event_type=${event_type}`;
    return api.get(url);
  },
  stats: (days = 30) => api.get(`/pixel/stats?days=${days}`),
  clientEvents: (clientId) => api.get(`/pixel/client/${clientId}`),
};


export const accountAPI = {
  getPixelId: () => api.get('/auth/account'),
  update: (data) => api.patch('/auth/account', data),
};


export const catalogAPI = {
  importYmlUrl: (url) => api.post(`/catalog/import/yml-url?url=${encodeURIComponent(url)}`),
  importYmlFile: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/catalog/import/yml-file', fd); },
  products: (skip = 0, limit = 50, category_id = null, search = null, available_only = false) => {
    let url = `/catalog/products?skip=${skip}&limit=${limit}&available_only=${available_only}`;
    if (category_id) url += `&category_id=${category_id}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return api.get(url);
  },
  categories: () => api.get('/catalog/categories'),
  stats: () => api.get('/catalog/stats'),
  deleteAll: () => api.delete('/catalog'),
};


export const automationsAPI = {
  triggers: () => api.get('/automations/triggers'),
  list: () => api.get('/automations'),
  create: (data) => api.post('/automations', data),
  update: (id, data) => api.patch(`/automations/${id}`, data),
  delete: (id) => api.delete(`/automations/${id}`),
  logs: (id, skip = 0, limit = 50) => api.get(`/automations/${id}/logs?skip=${skip}&limit=${limit}`),
  runNow: () => api.post('/automations/run-now'),
  clearLogs: (id) => api.delete(`/automations/${id}/logs`),
};


export const crmAPI = {
  reconcileSources:()=>api.post('/crm/sources/reconcile'),
  sources:()=>api.get('/crm/sources'),
  saveSources:data=>api.put('/crm/sources',data),
  types: () => api.get('/crm/types'),
  get: () => api.get('/crm/integration'),
  create: (data) => api.post('/crm/integration', data),
  update: (data) => api.patch('/crm/integration', data),
  delete: () => api.delete('/crm/integration'),
  clearSecret: () => api.delete('/crm/integration/secret'),
  stages: () => api.get('/crm/stages'),
  contactFields: () => api.get('/crm/contact-fields'),
  importClients: (limit = 0) => api.post('/crm/import/clients', { limit }),
  importOrders: (options = {}) => api.post('/crm/import/orders', { limit: 0, ...options }),
  taskStatus: (taskId) => api.get(`/crm/tasks/${taskId}`),
  cancelTask: (taskId) => api.post(`/crm/tasks/${taskId}/cancel`),
};


export const botsAPI = {
  // Единый каталог ботов (V1.1, read-only). Пока возвращает только MAX.
  list: () => api.get('/bots'),
};

export const maxAPI = {
  testUpload: (id, userId, file) => api.post(`/max/connections/${id}/test-messages/attachments`, file,
    { params: { filename: file.name, max_user_id: userId }, headers: { 'Content-Type': file.type || 'application/octet-stream' } }),
  testPrepare: (id, data) => api.post(`/max/connections/${id}/test-messages/prepare`, data),
  testSend: (id, data) => api.post(`/max/connections/${id}/test-messages/send`, data),
  testAttempts: (id) => api.get(`/max/connections/${id}/test-messages/attempts`),
  testPreview: (id, data) => api.post(`/max/connections/${id}/test-messages/preview`, data),
  testRehearse: (id, data) => api.post(`/max/connections/${id}/test-messages/rehearse`, data),
  testHistory: (id) => api.get(`/max/connections/${id}/test-messages`),
  listConnections: () => api.get('/max/connections'),
  createConnection: (data) => api.post('/max/connections', data),
  verify: (id) => api.post(`/max/connections/${id}/verify`),
  disconnect: (id) => api.post(`/max/connections/${id}/disconnect`),
  rotateToken: (id, token) => api.post(`/max/connections/${id}/rotate-token`, { token }),
  rotateIngestSecret: (id) => api.post(`/max/connections/${id}/rotate-ingest-secret`),
  subscribers: (id) => api.get(`/max/connections/${id}/subscribers`),
  stats: (id) => api.get(`/max/connections/${id}/stats`),
  importSubscribers: (id, subscribers) => api.post(`/max/connections/${id}/import-subscribers`, { subscribers }),
  // Единая MAX-аудитория (read-only)
  audience: () => api.get('/max/audience'),
  audienceMetrics: () => api.get('/max/audience/metrics'),
  audienceUser: (maxUserId) => api.get(`/max/audience/${encodeURIComponent(maxUserId)}`),
  crmConflicts: () => api.get('/max/audience/crm-conflicts'),
};

export const telegramAPI = {
  audience: (id, params) => api.get(`/telegram/connections/${id}/subscribers`, { params }),
  list: () => api.get('/telegram/connections'),
  connect: (data) => api.post('/telegram/connections', data),
  verify: (id) => api.post(`/telegram/connections/${id}/verify`),
  rotate: (id, token) => api.post(`/telegram/connections/${id}/rotate-token`, { token }),
  disconnect: (id) => api.post(`/telegram/connections/${id}/disconnect`),
};

export const entryAPI = {
 channelStatus: () => api.get('/entry-points/channels/status'),
 activateMax: (id,data) => api.post(`/entry-points/${id}/max/activate`,data),
 maxState: (id) => api.get(`/entry-points/${id}/max`),
 list: () => api.get('/entry-points'),
 create: (data) => api.post('/entry-points',data),
 update: (id,data) => api.put(`/entry-points/${id}`,data),
 publish: (id) => api.post(`/entry-points/${id}/publish`),
 unpublish: (id) => api.post(`/entry-points/${id}/unpublish`),
 activate: (id,data) => api.post(`/entry-points/${id}/telegram/activate`,data),
 botState: (id) => api.get(`/entry-points/${id}/telegram`),
};


export const platformBillingAPI = {
  invoices: () => api.get('/billing/platform/invoices'),
  issueInvoice: data => api.post('/billing/platform/invoices',data),
  invoicePdf: id => api.get(`/billing/platform/invoices/${id}/pdf`,{responseType:'blob'}),
  plans: () => api.get('/billing/platform/plans'),
  profile: () => api.get('/billing/platform/profile'),
  saveProfile: data => api.put('/billing/platform/profile', data),
};

export const expensesAPI = {
  list: params => api.get('/billing/expenses', { params }),
  campaigns: search => api.get('/billing/expenses/campaigns', { params: { search } }),
  template: () => api.get('/billing/expenses/template', { params:{allocated:true}, responseType: 'blob' }),
  preview: csv_text => api.post('/billing/expenses/preview', { csv_text }),
  import: (csv_text, preview_sha256) => api.post('/billing/expenses/import', { csv_text, preview_sha256, confirm: true }),
};

export const contactPolicyAPI = {
  get: () => api.get('/communications/policy'),
  save: data => api.put('/communications/policy', data),
  logs: params => api.get('/communications/policy/logs', { params }),
};

export const reportsAPI = {
 catalog:()=>api.get('/reports/catalog'), saved:(archived=false)=>api.get('/reports/saved',{params:{archived}}),
 create:data=>api.post('/reports/saved',data),update:(id,data)=>api.put(`/reports/saved/${id}`,data),archive:(id,data)=>api.post(`/reports/saved/${id}/archive`,data),
 preview:data=>api.post('/reports/preview',data),queue:data=>api.post('/reports/exports',data),jobs:(skip=0)=>api.get('/reports/exports',{params:{skip,limit:20}}),
 cancel:id=>api.post(`/reports/exports/${id}/cancel`),download:id=>api.get(`/reports/exports/${id}/download`,{responseType:'blob'}),
};

export const rfmHistoryAPI={
 audiencePreview:data=>api.post("/analytics/rfm-history/audience-preview",data),
 snapshots:(days=90)=>api.get('/analytics/rfm-history/snapshots',{params:{days}}),
 request:()=>api.post('/analytics/rfm-history/snapshots'),
 compare:(start_id,end_id)=>api.get('/analytics/rfm-history/compare',{params:{start_id,end_id}}),
 clients:(start_id,end_id,from_segment,to_segment,skip=0)=>api.get('/analytics/rfm-history/clients',{params:{start_id,end_id,from_segment,to_segment,skip,limit:25}}),
};

export const platformAPI = {
 list: (section,params) => api.get(`/platform/${section}`,{params}),
 approval: password => api.post('/platform/approval',{password}),
 template: () => api.get('/platform/statement-template',{responseType:'blob'}),
 previewStatement: content => api.post('/platform/statements/preview',{content}),
 importStatement: data => api.post('/platform/statements/import',data),
 addReceipt: data => api.post('/platform/receipts',data),
 candidates: id => api.get(`/platform/receipts/${id}/candidates`),
 previewMatch: (id,invoice_id) => api.post(`/platform/receipts/${id}/preview`,{invoice_id}),
 confirm: (id,data,token) => api.post(`/platform/receipts/${id}/confirm`,data,{headers:{'X-Platform-Approval':token}}),
 invoicePdf: id => api.get(`/platform/invoices/${id}/pdf`,{responseType:'blob'}),
};

export const promotionsAPI={
 products:(search='',ids='')=>api.get('/promotions/products',{params:{search,ids}}),
 list:()=>api.get('/promotions'),
 create:data=>api.post('/promotions',data),
 update:(id,data)=>api.put(`/promotions/${id}`,data),
 quote:data=>api.post('/promotions/quote',data),
};

export const growthAPI={
 analytics:(days=90)=>api.get('/referrals/analytics',{params:{days}}),
 referrals:(skip=0)=>api.get('/referrals',{params:{skip}}),
 settings:data=>api.put('/referrals/settings',data),
 code:data=>api.post('/referrals/code',data),
 cards:(skip=0)=>api.get('/gift-cards',{params:{skip}}),
 issue:data=>api.post('/gift-cards',data),
 reveal:id=>api.post(`/gift-cards/${id}/reveal`),
 status:(id,data)=>api.put(`/gift-cards/${id}/status`,data),
 operations:id=>api.get(`/gift-cards/${id}/operations`),
 quote:data=>api.post('/gift-cards/quote',data),
};

export const walletAPI={
 state:()=>api.get('/wallet'),
 settings:data=>api.put('/wallet/settings',data),
 links:()=>api.get('/wallet/links'),
 createLink:data=>api.post('/wallet/links',data),
 revoke:id=>api.post(`/wallet/links/${id}/revoke`),
};

export const returnsAPI={
 context:id=>api.get(`/returns/orders/${id}`),
 preview:data=>api.post('/returns/preview',data),
 create:data=>api.post('/returns',data),
 request:id=>api.get(`/returns/requests/${id}`),
};

export const marketingAPI={report:params=>api.get('/analytics/marketing',{params}),sources:search=>api.get('/analytics/marketing/sources',{params:{search}}),experiments:()=>api.get('/analytics/marketing/experiments'),configure:data=>api.post('/analytics/marketing/experiments',data),disable:(kind,id)=>api.delete('/analytics/marketing/experiments/'+kind+'/'+id),template:()=>api.get('/analytics/marketing/expenses/template'),expenses:()=>api.get('/analytics/marketing/expenses')};

export const workflowAPI={list:params=>api.get('/workflow-deliveries',{params})};

export const deliveryAPI = {
  list: params => api.get('/billing/deliveries', {params}),
  template: () => api.get('/billing/deliveries/template', {responseType:'blob'}),
  preview: csv_text => api.post('/billing/deliveries/preview', {csv_text}),
  save: (csv_text, preview_sha256) => api.post('/billing/deliveries/import', {csv_text,preview_sha256,confirm:true}),
  sync: () => api.post('/billing/deliveries/sync'),
};

export const mobilePushAPI = { identity: id => api.get(`/mobile-push/apps/${id}/identity`), analytics: id => api.get(`/mobile-push/apps/${id}/analytics`), saveReportingCredential: (id,data) => api.put(`/mobile-push/apps/${id}/analytics/credential`,data), syncAnalytics: (id,params) => api.post(`/mobile-push/apps/${id}/analytics/sync`,null,{params}), list: () => api.get('/mobile-push/apps'), create: data => api.post('/mobile-push/apps', data) };

export const pushCampaignAPI={cancel:id=>api.post(`/communications/push/runs/${id}/cancel`),runs:()=>api.get('/communications/push/runs'),launch:(id,data)=>api.post(`/communications/push/drafts/${id}/launch`,data),apps:()=>api.get('/communications/push/apps'),drafts:()=>api.get('/communications/push/drafts'),preview:data=>api.post('/communications/push/preview',data),create:data=>api.post('/communications/push/drafts',data),update:(id,data)=>api.put(`/communications/push/drafts/${id}`,data)};

export const publicationAPI={channels:()=>api.get('/communications/publications/channels'),connections:()=>api.get('/communications/publications/connections'),addChannel:data=>api.post('/communications/publications/channels',data),disableChannel:id=>api.delete(`/communications/publications/channels/${id}`),posts:()=>api.get('/communications/publications/posts'),create:data=>api.post('/communications/publications/posts',data),launch:(id,data)=>api.post(`/communications/publications/posts/${id}/launch`,data),cancel:id=>api.post(`/communications/publications/posts/${id}/cancel`)};
